/** #901. Core GameState admission, independent of transport and display.
 * A connection owner supplies authority/epoch; data cannot elect either.
 * No network, timers, source ranking, alerts or execution authority here.
 */
export interface SourceSummary {
  source: string;
  statusId: 1 | 2 | 3;
  awayScore: number | null;
  homeScore: number | null;
  gameClock: string | null;
  periodTypeId: number | null;
  periodLabel?: string | null;
  finalReason: 'reported' | 'zombie' | null;
  lastUpdateMs: number;
  lastStateChangeMs: number;
}

export interface CoreGameState extends SourceSummary {
  gameKey: string;
  sport: string;
  sourceEventId: string;
  identityReason: string;
  awayTeam: string;
  homeTeam: string;
  eventStart: string;
  stale: boolean;
  disputed: boolean;
  /** REST summaries are not complete independent provider records. */
  sources: readonly SourceSummary[] | null;
}

type ObjectValue = Record<string, unknown>;
const object = (v: unknown): v is ObjectValue => typeof v === 'object' && v !== null && !Array.isArray(v);
const text = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const stamp = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 8.64e15;
const integer = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
const score = (v: unknown): v is number | null => v === null || integer(v);
const nullableText = (v: unknown): v is string | null => v === null || typeof v === 'string';
// Core v2 keys use ncaaf while the same record's sport field uses CFB.
// This is the existing SPORT_NORMALIZE alias; do not fold unrelated soccer
// leagues or tennis tours just because they share a Dashboard sport bucket.
const wireSport = (v: string): string => v.toLowerCase() === 'cfb' ? 'ncaaf' : v.toLowerCase();

/** Canonical v2 identity only; never alias a doubleheader to a three-part key. */
export function parseGameIdentity(key: unknown): { sport: string; teams: readonly string[] } | null {
  if (typeof key !== 'string') return null;
  const parts = key.split('|');
  if (parts.length !== 5) return null;
  const [sport, date, gameNo, lo, hi] = parts;
  if (!/^[a-z][a-z0-9-]*$/.test(sport) || !/^\d{4}-\d{2}-\d{2}$/.test(date)
    || !/^[1-9]\d*$/.test(gameNo) || !Number.isSafeInteger(Number(gameNo))
    || !text(lo) || !text(hi) || lo >= hi) return null;
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(ms) || new Date(ms).toISOString().slice(0, 10) !== date) return null;
  return { sport, teams: [lo, hi] };
}

function parseSummary(v: unknown): SourceSummary | null {
  if (!object(v) || !text(v.source) || ![1, 2, 3].includes(v.statusId as number)
    || !score(v.awayScore) || !score(v.homeScore) || !nullableText(v.gameClock)
    || !(v.periodTypeId === null || integer(v.periodTypeId))
    || !(v.periodLabel === undefined || nullableText(v.periodLabel))
    || !(v.finalReason === null || v.finalReason === 'reported' || v.finalReason === 'zombie')
    || !stamp(v.lastUpdateMs) || !stamp(v.lastStateChangeMs)) return null;
  if (v.statusId !== 3 && v.finalReason !== null) return null;
  return {
    source: v.source, statusId: v.statusId as 1 | 2 | 3,
    awayScore: v.awayScore, homeScore: v.homeScore, gameClock: v.gameClock,
    periodTypeId: v.periodTypeId as number | null,
    ...(v.periodLabel !== undefined ? { periodLabel: v.periodLabel as string | null } : {}),
    finalReason: v.finalReason as SourceSummary['finalReason'],
    lastUpdateMs: v.lastUpdateMs, lastStateChangeMs: v.lastStateChangeMs,
  };
}

/** Whitelist only verified fields. In particular, do not relabel Core's
 * consensus as Unabated or infer named tennis scores from anonymous A/B. */
export function parseCoreGameState(v: unknown, key: string): CoreGameState | null {
  const identity = parseGameIdentity(key);
  const summary = parseSummary(v);
  if (!identity || !summary || !object(v) || v.gameKey !== key
    || typeof v.sport !== 'string' || wireSport(v.sport) !== wireSport(identity.sport)
    || !text(v.awayTeam) || !text(v.homeTeam) || v.awayTeam === v.homeTeam
    || !identity.teams.includes(v.awayTeam) || !identity.teams.includes(v.homeTeam)
    || !text(v.sourceEventId) || !text(v.identityReason)
    || !text(v.eventStart) || !Number.isFinite(Date.parse(v.eventStart))
    || typeof v.stale !== 'boolean' || typeof v.disputed !== 'boolean') return null;
  let sources: SourceSummary[] | null = null;
  if (v.sources !== undefined) {
    if (!Array.isArray(v.sources)) return null;
    sources = [];
    const seen = new Set<string>();
    for (const raw of v.sources) {
      const source = parseSummary(raw);
      if (!source || seen.has(source.source)) return null;
      seen.add(source.source);
      sources.push(source);
    }
  }
  return { ...summary, gameKey: key, sport: identity.sport, sourceEventId: v.sourceEventId,
    identityReason: v.identityReason, awayTeam: v.awayTeam, homeTeam: v.homeTeam,
    eventStart: v.eventStart, stale: v.stale, disputed: v.disputed, sources };
}

export interface AdmittedGameState {
  rev: number;
  state: CoreGameState | null;
  /** Source observation/change clocks live in state, separate from these. */
  publishedAtMs: number;
  receivedAtMs: number;
  withdrawal: 'tombstone' | 'snapshot-omission' | 'invalid-record' | null;
}

export interface GameStateSession { readonly authority: string; readonly epoch: string }
export interface GameStateRestRead {
  readonly session: GameStateSession;
  readonly keys: readonly string[];
  readonly version: number;
  readonly withdrawals: ReadonlyMap<string, number>;
  readonly rows: ReadonlyMap<string, AdmittedGameState | undefined>;
  readonly sequence: number;
}
export interface GameStateComparison {
  sources: readonly SourceSummary[];
  invalidSources?: number;
  publishedAtMs: number;
  receivedAtMs: number;
}
interface PendingSnapshot {
  generation: number;
  sports: ReadonlySet<string> | null;
  rows: Map<string, AdmittedGameState>;
  live: Set<string>;
  valid: boolean;
}

/** A fresh instance is owned by one adapter, not shared between endpoints.
 * Session object identity fences late callbacks even when an epoch is reused.
 */
export class GameStateAdmission {
  private session: GameStateSession | null = null;
  private records = new Map<string, AdmittedGameState>();
  private sourceCollections = new Map<string, GameStateComparison>();
  private pending: PendingSnapshot | null = null;
  private generation = -1;
  private version = 0;
  private withdrawals = new Map<string, number>();
  private usedReads = new WeakSet<GameStateRestRead>();
  private readSequence = 0;
  private completedReads = new Map<string, number>();

  connect(authority: string, epoch: string): GameStateSession {
    if (!text(authority) || !text(epoch)) throw new Error('GameState authority and epoch are required');
    this.session = Object.freeze({ authority, epoch });
    this.records = new Map();
    this.sourceCollections = new Map();
    this.withdrawals = new Map();
    this.completedReads = new Map();
    this.pending = null;
    this.generation = -1;
    this.version++;
    return this.session;
  }

  disconnect(session: GameStateSession): void {
    if (session !== this.session) return;
    this.session = null;
    this.pending = null;
    // Retained records are last-known display evidence, not connected state.
  }

  isConnected(): boolean { return this.session !== null; }
  snapshot(): ReadonlyMap<string, AdmittedGameState> { return new Map(this.records); }
  /** A selected-only WS update neither replaces nor refreshes this collection. */
  comparisons(): ReadonlyMap<string, GameStateComparison> { return new Map(this.sourceCollections); }

  /** The adapter must request these exact v2 keys from the admitted Core.
   * Legacy aliases are deliberately not resolved in this boundary.
   */
  startRest(session: GameStateSession, keys: readonly string[]): GameStateRestRead | null {
    if (session !== this.session || keys.some(key => !parseGameIdentity(key))) return null;
    return Object.freeze({ session, keys: Object.freeze([...new Set(keys)]), version: this.version,
      withdrawals: new Map(keys.map(key => [key, this.withdrawals.get(key) ?? 0])),
      rows: new Map(keys.map(key => [key, this.records.get(key)])), sequence: ++this.readSequence });
  }

  /** One complete HTTP batch. Session/snapshot boundaries invalidate the
   * ticket; live revisions do not prevent independent source comparisons.
   * A withdrawal after issuance fences that key, including later recovery.
   */
  finishRest(read: GameStateRestRead, raw: unknown, receivedAtMs: number): boolean {
    if (read.session !== this.session || read.version !== this.version || this.pending || this.usedReads.has(read)
      || !object(raw) || !object(raw.states) || !stamp(receivedAtMs)) return false;
    const publishedAtMs = typeof raw.generatedAt === 'string' ? Date.parse(raw.generatedAt) : raw.serverTs;
    if (!stamp(publishedAtMs)) return false;
    const next = new Map(this.records);
    const comparisons = new Map(this.sourceCollections);
    const withdrawals = new Map(this.withdrawals);
    const completed = new Map(this.completedReads);
    let applied = false;
    for (const key of read.keys) {
      if (!Object.prototype.hasOwnProperty.call(raw.states, key)) return false;
      const value = raw.states[key];
      const old = next.get(key);
      // Validate epoch before skipping a changed key. A mixed authority batch
      // is never silently accepted as a partly current response.
      if (object(value) && value.sourceEpoch !== read.session.epoch) return false;
      if ((read.withdrawals.get(key) ?? 0) !== (this.withdrawals.get(key) ?? 0)) continue;
      if ((completed.get(key) ?? 0) >= read.sequence) continue;
      completed.set(key, read.sequence);
      if (value === null) {
        // REST null has no revision. It cannot withdraw a WS record that
        // arrived after the request's structural baseline; the adapter uses
        // WS tombstones for that authority. Keep last-known selected state.
        if (old?.state && old !== read.rows.get(key)) continue;
        comparisons.delete(key);
        next.set(key, { rev: old?.rev ?? -1, state: null, publishedAtMs,
          receivedAtMs, withdrawal: 'tombstone' });
        withdrawals.set(key, (withdrawals.get(key) ?? 0) + 1);
        applied = true;
        continue;
      }
      if (!object(value) || value.sourceEpoch !== read.session.epoch || !integer(value.rev)) return false;
      // A provider summary is independent evidence, not part of the selected
      // record's validity. A bad summary must not withdraw a valid WS score.
      const state = parseCoreGameState({ ...value, sources: undefined }, key);
      if (!state) {
        if (old && old.rev > value.rev) continue;
        comparisons.delete(key);
        next.set(key, { rev: value.rev, state: null, publishedAtMs, receivedAtMs, withdrawal: 'invalid-record' });
        withdrawals.set(key, (withdrawals.get(key) ?? 0) + 1);
        applied = true;
        continue;
      }
      const summaries = Array.isArray(value.sources) ? value.sources.map(parseSummary) : [null];
      const counts = new Map<string, number>();
      for (const source of summaries) if (source) counts.set(source.source, (counts.get(source.source) ?? 0) + 1);
      const sources = summaries.filter((source): source is SourceSummary => source !== null && counts.get(source.source) === 1);
      const comparison = { sources, invalidSources: summaries.length - sources.length, publishedAtMs, receivedAtMs };
      if (old && value.rev <= old.rev && old.state === null && old.withdrawal !== 'invalid-record') continue;
      if (old?.state && value.rev < old.rev) {
        comparisons.set(key, comparison);
        applied = true;
        continue;
      }
      if (old?.state && value.rev === old.rev) {
        const { sources: _oldSources, ...oldSelected } = old.state;
        const { sources: _newSources, ...newSelected } = state;
        if (JSON.stringify(oldSelected) !== JSON.stringify(newSelected)) return false;
      }
      next.set(key, { rev: value.rev, state, publishedAtMs, receivedAtMs, withdrawal: null });
      comparisons.set(key, comparison);
      applied = true;
    }
    this.usedReads.add(read);
    this.records = next;
    this.sourceCollections = comparisons;
    this.withdrawals = withdrawals;
    this.completedReads = completed;
    return applied;
  }

  /** Scope must be the accepted subscription's scope, captured by the adapter.
   * Begin does not carry it on the wire. Never use a desired/unacknowledged scope.
   */
  begin(session: GameStateSession, frame: unknown, sports: readonly string[] | null): boolean {
    if (session !== this.session || !object(frame) || frame.type !== 'gsSnapshotBegin'
      || frame.sourceEpoch !== session.epoch || !integer(frame.generation)
      || frame.generation <= this.generation) return false;
    this.generation = frame.generation;
    this.version++;
    this.pending = { generation: frame.generation,
      sports: sports === null ? null : new Set(sports.map(s => s.toLowerCase())),
      rows: new Map(), live: new Set(), valid: true };
    return true;
  }

  accept(session: GameStateSession, frame: unknown, receivedAtMs: number): boolean {
    if (session !== this.session || !object(frame) || frame.type !== 'gameState'
      || frame.sourceEpoch !== session.epoch) return false;
    const pending = this.pending;
    const tagged = frame.generation !== undefined;
    if (tagged && (!pending || frame.generation !== pending.generation)) return false;
    const identity = parseGameIdentity(frame.ticker);
    const state = frame.state === null ? null : parseCoreGameState(frame.state, String(frame.ticker));
    if (!identity || !integer(frame.rev) || !stamp(frame.serverTs) || !stamp(receivedAtMs)
      || (tagged && pending?.sports && !pending.sports.has(identity.sport))) {
      if (tagged && pending) pending.valid = false;
      return false;
    }
    const key = frame.ticker as string;
    const row: AdmittedGameState = { rev: frame.rev, state, publishedAtMs: frame.serverTs,
      receivedAtMs, withdrawal: frame.state === null ? 'tombstone' : state === null ? 'invalid-record' : null };
    if (tagged && pending) {
      if (pending.rows.has(key)) { pending.valid = false; return false; }
      pending.rows.set(key, row);
      return true;
    }
    const previous = this.records.get(key);
    if (previous && (previous.rev > row.rev || (previous.rev === row.rev
      && !(previous.withdrawal === 'invalid-record' && row.state !== null)))) return false;
    this.records.set(key, row);
    if (state === null) {
      this.sourceCollections.delete(key);
      this.withdrawals.set(key, (this.withdrawals.get(key) ?? 0) + 1);
    }
    pending?.live.add(key);
    return true;
  }

  end(session: GameStateSession, frame: unknown, receivedAtMs: number): boolean {
    const pending = this.pending;
    if (session !== this.session || !pending || !object(frame)
      || frame.type !== 'gsSnapshotEnd' || frame.sourceEpoch !== session.epoch
      || frame.generation !== pending.generation) return false;
    this.pending = null;
    if (!pending.valid || !integer(frame.gameCount) || frame.gameCount !== pending.rows.size
      || !stamp(frame.serverTs) || !stamp(receivedAtMs)) return false;
    const next = new Map(this.records);
    for (const [key, old] of next) {
      const inScope = pending.sports === null || pending.sports.has(parseGameIdentity(key)!.sport);
      if (inScope && !pending.rows.has(key) && !pending.live.has(key)) {
        next.set(key, { ...old, state: null, withdrawal: 'snapshot-omission',
          publishedAtMs: frame.serverTs, receivedAtMs });
      }
    }
    for (const [key, row] of pending.rows) {
      const old = next.get(key);
      // A live interleave or a prior tombstone wins on equal revision.
      if (!old || row.rev > old.rev || (old.withdrawal === 'invalid-record' && row.rev === old.rev && row.state)) next.set(key, row);
    }
    this.records = next;
    for (const [key, row] of next) if (row.state === null) this.sourceCollections.delete(key);
    this.version++;
    return true;
  }
}
